import React, { useEffect, useRef, useState } from "react";

export const SEND_CHANNELS = [
  { id: "gmail", label: "Gmail" },
  { id: "slack", label: "Slack" },
];

/**
 * One "Send" control that opens a channel picker instead of a button per medium.
 *
 * Add future delivery channels to ``SEND_CHANNELS`` — the toolbar stays one
 * button. Already-sent channels drop out of the menu. A single remaining
 * channel skips the menu and opens that picker directly.
 *
 * @example
 * <SendChannelMenu
 *   sent={{ gmail: false, slack: true }}
 *   onSelect={(id) => id === "gmail" && openGmail()}
 * />
 * // Clicking Send opens the Gmail picker (Slack already sent)
 */
const SendChannelMenu = ({
  iconOnly = false,
  disabled = false,
  loading = false,
  sent = {},
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const remaining = SEND_CHANNELS.filter((channel) => !sent[channel.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (remaining.length === 0) return null;

  const onlyChannel = remaining.length === 1 ? remaining[0] : null;
  const label = loading
    ? "Sending…"
    : onlyChannel
      ? `Send via ${onlyChannel.label}`
      : "Send ▾";

  const handleTriggerClick = () => {
    if (loading || disabled) return;
    if (onlyChannel) {
      onSelect?.(onlyChannel.id);
      return;
    }
    setOpen((current) => !current);
  };

  return (
    <div className="ai-draft-expanded__send-menu" ref={rootRef}>
      <button
        type="button"
        className={`ai-draft-expanded__send-btn${
          iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
        }`}
        onClick={handleTriggerClick}
        disabled={disabled || loading}
        title="Choose where to send this draft"
        aria-label={onlyChannel ? label : "Send"}
        aria-expanded={onlyChannel ? undefined : open}
        aria-haspopup={onlyChannel ? undefined : "menu"}
      >
        {iconOnly ? (
          <span className="ai-icon-action-btn__glyph" aria-hidden="true">
            ✉
          </span>
        ) : (
          <span className="ai-text-action-btn__label">{label}</span>
        )}
      </button>
      {open && !onlyChannel ? (
        <ul className="ai-draft-expanded__send-menu-list" role="menu">
          {remaining.map((channel) => (
            <li key={channel.id} role="none">
              <button
                type="button"
                role="menuitem"
                className="ai-draft-expanded__send-menu-item"
                onClick={() => {
                  setOpen(false);
                  onSelect?.(channel.id);
                }}
              >
                {channel.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default SendChannelMenu;
